// "Next follow-up" for a sent quotation / proforma (client, 2026-10-29): it
// must always carry one until it moves on to a proforma or an invoice. For the
// document's own salesperson this cannot be dismissed; an admin or manager
// adding it on their behalf may close it.
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Button } from '@/shared/components/ui/Button';
import { Modal } from '@/shared/components/ui/Modal';
import { useCreateTimelineRow, type ParentType } from '@/features/activity/hooks/useFollowups';

/** Tomorrow 11:00, as a datetime-local value. */
function defaultNext() {
  const d = new Date(Date.now() + 24 * 3600 * 1000);
  d.setHours(11, 0, 0, 0);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

const METHODS = [
  { value: 'call', label: 'Call' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'email', label: 'Email' },
  { value: 'visit', label: 'Visit' },
  { value: 'other', label: 'Other' },
];

const input = 'rounded-lg border border-outline bg-surface px-2.5 py-1.5';

interface Props {
  parentType: ParentType;
  parentId: string;
  /** e.g. "Quotation RBM/26-27/6262 — Krishna Machine Tools" */
  title: string;
  backTo: string;
  required?: boolean;
  onClose?: () => void;
}

export function NextFollowupDialog({ parentType, parentId, title, backTo, required = true, onClose }: Props) {
  const navigate = useNavigate();
  const create = useCreateTimelineRow('followups');
  const [when, setWhen] = useState(defaultNext);
  const [method, setMethod] = useState('call');
  const [note, setNote] = useState('');
  const [error, setError] = useState('');

  const save = () => {
    setError('');
    if (!when) { setError('Pick the date of the next follow-up.'); return; }
    if (new Date(when).getTime() < Date.now() - 60_000) { setError('The next follow-up must be in the future.'); return; }
    if (!note.trim()) { setError('Write what the next follow-up is for.'); return; }
    create.mutate(
      { parentType, parentId, followupDate: new Date(when).toISOString(), followupNote: note.trim(), contactMethod: method, status: 'open' },
      {
        onSuccess: () => { toast.success('Next follow-up saved.'); onClose?.(); },
        onError: () => setError('Could not save the follow-up. Try again.'),
      },
    );
  };

  return (
    <Modal
      open
      onClose={required ? () => { /* required — saved, or left via "Back" */ } : (onClose ?? (() => {}))}
      closeOnBackdrop={false}
      dismissible={!required}
      size="lg"
      title={`Next follow-up — ${title}`}
      footer={(
        <div className="flex w-full items-center justify-between gap-3">
          <p className="text-body-sm text-on-surface-variant">A sent quotation needs a next follow-up until it moves to a proforma or an invoice.</p>
          {required
            ? <Button variant="secondary" onClick={() => navigate(backTo)}>Back</Button>
            : <Button variant="secondary" onClick={onClose}>Close</Button>}
        </div>
      )}
    >
      <div className="grid gap-3 sm:grid-cols-[14rem_10rem_1fr]">
        <label className="flex flex-col gap-1 text-body-sm">
          <span className="font-medium">When *</span>
          <input id="doc-next-when" type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} className={input} />
        </label>
        <label className="flex flex-col gap-1 text-body-sm">
          <span className="font-medium">How</span>
          <select id="doc-next-method" value={method} onChange={(e) => setMethod(e.target.value)} className={input}>
            {METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-body-sm">
          <span className="font-medium">Note *</span>
          <input id="doc-next-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Call to confirm the order" className={input} />
        </label>
        <div className="sm:col-span-3 flex items-center justify-between gap-3">
          <span className="text-body-sm text-danger-text">{error}</span>
          <Button onClick={save} disabled={create.isPending}>Save follow-up</Button>
        </div>
      </div>
    </Modal>
  );
}
